import React, { useState } from 'react';
import { Clock, Droplet, Plus, CheckCircle, Calendar } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика потребления воды в течение дня
 */
const IntakeSchedule = ({ schedule, results, updateWaterConsumptionData }) => {
  const [completedItems, setCompletedItems] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  if (!schedule || schedule.length === 0 || !results) {
    return null;
  }

  // Обработчик отметки о потреблении воды
  const handleMarkAsCompleted = (index) => {
    if (completedItems.includes(index)) {
      return; // Если уже отмечено, ничего не делаем
    }
    
    // Обновляем локальное состояние
    setCompletedItems([...completedItems, index]);
    
    // Обновляем данные о потреблении воды за выбранную дату
    updateWaterConsumptionData(selectedDate, schedule[index].amount);
  };

  // Изменение даты для трекинга
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    // Сбрасываем отметки при смене даты
    setCompletedItems([]);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Clock className="w-5 h-5 mr-2 text-blue-500" />
          График потребления воды
        </h3>

        {/* Выбор даты для трекинга потребления */}
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-blue-500" />
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            className="form-input py-1 px-2 text-sm"
            max={new Date().toISOString().split('T')[0]}
          />
        </div>
      </div>

      {/* Информационное сообщение */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Droplet className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>Оптимальный график потребления воды для вас на основе расчета: {formatNumber(results.recommendedWaterIntakeMl, 0)} мл в день.</p>
          <p className="mt-1 text-xs text-blue-500">Отмечайте выпитую воду, чтобы отслеживать свой прогресс.</p>
        </div>
      </div>

      {/* График потребления */}
      <div className="space-y-3">
        {schedule.map((item, index) => (
          <div 
            key={index} 
            className={`flex items-center justify-between p-3 rounded-lg border transition-colors 
              ${completedItems.includes(index) 
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/10'}`
            }
          >
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 flex-shrink-0 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
                <Droplet className={`w-5 h-5 ${completedItems.includes(index) ? 'text-green-500' : 'text-blue-500'}`} />
                {completedItems.includes(index) && (
                  <CheckCircle className="absolute -bottom-1 -right-1 w-4 h-4 text-green-500 bg-white dark:bg-gray-800 rounded-full" />
                )}
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">{item.time}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{item.note}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="font-medium text-gray-900 dark:text-white">{item.amount} мл</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{item.percentOfDaily}% от дневной нормы</div>
              </div>
              
              {!completedItems.includes(index) ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleMarkAsCompleted(index)}
                  icon={<Plus className="w-4 h-4" />}
                >
                  Выпито
                </Button>
              ) : (
                <span className="text-xs font-medium text-green-600 dark:text-green-400 py-1.5 px-3">
                  Выполнено
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Прогресс дня */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-800/80 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Прогресс за {new Date(selectedDate).toLocaleDateString('ru-RU')}
          </div>
          <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
            {completedItems.length > 0 
              ? `${formatNumber(completedItems.reduce((sum, index) => sum + schedule[index].amount, 0), 0)} мл из ${formatNumber(results.recommendedWaterIntakeMl, 0)} мл`
              : `0 мл из ${formatNumber(results.recommendedWaterIntakeMl, 0)} мл`
            }
          </div>
        </div>
        
        {/* Прогресс-бар */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
          <div 
            className="bg-blue-500 h-2.5 rounded-full transition-all duration-500" 
            style={{ 
              width: `${completedItems.length > 0 
                ? Math.min(100, (completedItems.reduce((sum, index) => sum + schedule[index].amount, 0) / results.recommendedWaterIntakeMl) * 100) 
                : 0}%` 
            }}
          ></div>
        </div>
        
        {/* Мотивационное сообщение */}
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {completedItems.length === 0 && "Отметьте первый прием воды, чтобы начать отслеживание!"}
          {completedItems.length > 0 && completedItems.length < schedule.length / 2 && "Хорошее начало! Продолжайте в том же духе."}
          {completedItems.length >= schedule.length / 2 && completedItems.length < schedule.length && "Уже больше половины! Вы молодец!"}
          {completedItems.length === schedule.length && "Отличная работа! Вы выполнили дневную норму воды!"}
        </div>
      </div>
    </div>
  );
};

export default IntakeSchedule;